import { Form, useForm } from '@/components/forms';
import Input from '@/components/forms/Input';
import Textarea from '@/components/forms/Textarea';
import { descriptionRegex, nameValidationRegex } from '@/constants/regex';
import { useDynamicLocale } from '@/hoslog-intl';
import { z } from 'zod';
import useAddMainPageGroup from '../hooks/useAddMainPageGroup';
import useUpdateMainPageGroup from '../hooks/useUpdateMainPageGroup';

const addGroupModalSchema = z.object({
  groupName: z
    .string()
    .min(1, { message: 'common.form.validation.required' })
    .refine((val) => val == null || val === '' || nameValidationRegex.test(val), {
      message: 'common.form.validation.nameOnly',
    }),
  description: z
    .string()
    .refine((val) => val == null || val === '' || descriptionRegex.test(val), {
      message: 'common.form.validation.nameOnly',
    })
    .optional(),
});

export const MainPageGroupForm = ({
  locationId,
  defaultValues,
  groupId,
}: {
  locationId?: string;
  defaultValues?: { groupName: string; description: string };
  groupId?: string;
}) => {
  const form = useForm({ schema: addGroupModalSchema, mode: 'onBlur', defaultValues });
  const { t } = useDynamicLocale();
  const { mutateAsyncAddMainPageGroup } = useAddMainPageGroup();
  const { mutateAsyncUpdateMainPageGroup } = useUpdateMainPageGroup();
  return (
    <Form
      form={form}
      onSubmit={async (data) => {
        if (locationId) {
          const payload = {
            locationId,
            groupName: data.groupName,
            description: data.description ?? '',
          };
          await mutateAsyncAddMainPageGroup(payload);
        } else if (groupId && defaultValues) {
          const payload = {
            groupId,
            groupName: data.groupName,
            description: data.description ?? '',
          };
          await mutateAsyncUpdateMainPageGroup(payload);
        }
      }}
    >
      <div className="space-y-[18px]">
        <Input
          label="common.form.label.groupName"
          {...form.register('groupName')}
          placeholder={t('common.form.placeholder.groupName')}
        />
        <Textarea
          label="common.form.label.description"
          {...form.register('description')}
          placeholder={t('common.form.placeholder.groupDescription')}
          isRequired={false}
        />
      </div>
    </Form>
  );
};
